/**
 * Modern Monitor component using React hooks and TypeScript
 * Replaces the legacy Monitor.jsx with recompose HOCs
 * Uses exact same transformations as the legacy implementation
 */

import React, { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { type ApolloClient, type NormalizedCacheObject, useApolloClient } from '@apollo/client';
import { withRouter } from 'react-router';
import CacheManager from '@/graph/cacheManager';
import { withScope } from '@/contexts/ScopeContext';
import { withSession } from '@/state/Session';
import { unpackSessionObject } from '../../unpackSessionObject';
import ClientUpdater from '../../../ClientUpdater';
import MonitorContent from './MonitorContent';
import ScheduleColumn from './ScheduleColumn';
import { useMonitorData } from './MonitorModern.data';
import { useAlerts } from './hooks/useAlerts';
import type { MonitorConfiguration, Scope, Session } from './monitor.types';

// Constants
const DEFAULT_PATIENT_NAME_FORMAT = 'fullName';
const DEFAULT_SHOW_PROCEDURE = true;

interface MonitorProps {
  staffMode?: boolean;
  scope?: Scope;
  session?: Session;
  patientNameFormat?: string;
  isLiaison?: boolean;
  isSuperAdmin?: boolean;
  isKiosk?: boolean;
  configuration?: MonitorConfiguration;
}

// Props type for the HOC-wrapped component (includes injected props from withRouter, withSession, withScope)
interface MonitorWithHOCsProps extends MonitorProps {
  // Props injected by withRouter
  history?: any;
  location?: any;
  match?: any;
  // Props injected by withSession and withScope are already in MonitorProps
  scope?: Scope;
  session?: Session;
  configuration?: MonitorConfiguration;
}

/**
 * Modern Monitor component with exact same transformations as legacy Monitor.jsx
 */
const MonitorModern: React.FC<MonitorProps> = ({
  staffMode = false,
  scope,
  session,
  patientNameFormat = DEFAULT_PATIENT_NAME_FORMAT,
  isLiaison = false,
  isSuperAdmin = false,
  isKiosk = false,
  configuration = {},
}) => {
  const client = useApolloClient();
  const cacheManagerRef = useRef<CacheManager | null>(null);

  // Extract required values from scope and session
  const hospitalId = scope?.hospital?.id || '';
  const groupId = scope?.hospital?.groupId || '';
  const showProcedure = configuration?.showProcedure ?? DEFAULT_SHOW_PROCEDURE;

  // Fetch and process monitor data using our custom hook with exact same transformations
  const processedData = useMonitorData(patientNameFormat, hospitalId, groupId, isLiaison, showProcedure, configuration);

  // Setup alerts using the existing unified hook
  useAlerts({
    ...processedData,
    loading: processedData.loading,
    configuration,
  });

  // Initialize and manage cache manager
  useEffect(() => {
    if (!cacheManagerRef.current && client) {
      console.log('🔧 Initializing CacheManager');
      cacheManagerRef.current = new CacheManager(client as ApolloClient<NormalizedCacheObject>);
    }
  }, [client]);

  // Create a stable roomData object
  const roomData = useMemo(
    () => ({
      waitingRoom: processedData.waitingRoom,
      preOp: processedData.preOp,
      pacu: processedData.pacu,
      postOps: processedData.postOps,
      operationRooms: processedData.operationRooms,
    }),
    [
      processedData.waitingRoom,
      processedData.preOp,
      processedData.pacu,
      processedData.postOps,
      processedData.operationRooms,
    ]
  );

  // Monitor cache and purge if needed
  useEffect(() => {
    if (cacheManagerRef.current && !processedData.loading) {
      cacheManagerRef.current.monitorAndPurge(roomData);
    } else if (!cacheManagerRef.current) {
      console.log('❌ CacheManager not initialized - no Apollo client available');
    }
  }, [roomData, processedData.loading, cacheManagerRef]);

  // Memoized filter functions
  const filterPatients = useCallback(
    (room: any) => {
      if (!room || !room.patients) return room;
      if (!configuration?.showPatientTypes || configuration.showPatientTypes.length === 0) {
        return room;
      }
      return { ...room, patients: room.patients.filter((p: any) => configuration.showPatientTypes?.includes(p.type)) };
    },
    [configuration]
  );

  const filterOperationRoom = useCallback(
    (room: any) => {
      if (!room) return room;
      if (!configuration?.showPatientTypes || configuration.showPatientTypes.length === 0) {
        return room;
      }
      return {
        ...room,
        patient: room.patient && configuration.showPatientTypes.includes(room.patient.type) ? room.patient : null,
        patients: room.patients
          ? room.patients.filter((p: any) => configuration.showPatientTypes?.includes(p.type))
          : [],
      };
    },
    [configuration]
  );

  // Apply client-side filtering (same as legacy render method)
  const filteredData = useMemo(() => {
    const { operationRooms, waitingRoom, preOp, pacu, postOps } = processedData;

    const filteredOperationRooms = operationRooms.map(filterOperationRoom);
    const filteredWaitingRoom = filterPatients(waitingRoom);
    const filteredPreOp = filterPatients(preOp);
    const filteredPacu = filterPatients(pacu);
    const filteredPostOps = filterPatients(postOps);

    return {
      operationRooms: filteredOperationRooms,
      waitingRoom: filteredWaitingRoom,
      preOp: filteredPreOp,
      pacu: filteredPacu,
      postOps: filteredPostOps,
    };
  }, [processedData, filterPatients, filterOperationRoom]);

  // Order operation rooms (same as legacy logic)
  const orderedRooms = useMemo(() => {
    const roomsWithOrder = filteredData.operationRooms
      .filter((room: any) => room?.order !== null)
      .sort((a: any, b: any) => a.order - b.order);
    const roomsWithoutOrder = filteredData.operationRooms.filter((room: any) => room?.order === null);

    return [
      ...roomsWithOrder,
      ...roomsWithoutOrder.filter((room: any) => room?.name?.toUpperCase()?.startsWith('OR')),
      ...roomsWithoutOrder.filter((room: any) => !room?.name?.toUpperCase()?.startsWith('OR')),
    ];
  }, [filteredData.operationRooms]);

  // Derived values for rendering
  const derivedValues = useMemo(() => {
    const hasPreOpPriorityModule = scope?.hospital?.modules?.preOpPriority?.hasPreOpPriority || false;
    const hasNoteModule = scope?.hospital?.modules?.noteTablet || false;
    const hasPostop = scope?.hospital?.modules?.hasPostop && configuration?.showPostOp !== false;
    const staffCostAccess = session?.account?.metadata?.staffCostAccess;
    const showStaffTracking =
      scope?.hospital?.modules?.showStaffTracking &&
      (isSuperAdmin || staffCostAccess || (configuration?.showStaffIndicators && isKiosk));

    return {
      hasPreOpPriorityModule,
      hasNoteModule,
      hasPostop,
      showStaffTracking,
    };
  }, [scope, session, configuration, isSuperAdmin, isKiosk]);

  // Check if client updater should be shown
  const shouldShowClientUpdater = useMemo(
    () =>
      [...orderedRooms, filteredData.waitingRoom, filteredData.preOp, filteredData.pacu, filteredData.postOps]
        .filter((room: any) => room && room.patients)
        .every(({ patients = [] }) => patients.length <= 0),
    [orderedRooms, filteredData]
  );

  // Common props for MonitorContent
  const monitorContentProps = useMemo(
    () => ({
      scheduleColumn: configuration?.showScheduleColumn ? (
        <ScheduleColumn
          timezone={scope?.hospital?.timezone?.id}
          view={configuration?.scheduleColumnView}
          widthPercent={configuration?.scheduleColumnWidth}
          maxColumns={configuration?.scheduleColumnMaxColumns}
          groupByTime={configuration?.scheduleColumnGroupByTime}
          groupByPhysician={configuration?.scheduleColumnGroupByPhysician}
          carouselIntervalSeconds={configuration?.scheduleColumnCarouselIntervalSeconds}
          showProcedure={showProcedure}
          showScheduledOr={configuration?.patientCardShowScheduledOr}
          isKiosk={isKiosk}
        />
      ) : undefined,
      operationRooms: orderedRooms,
      waitingRoom: filteredData.waitingRoom,
      preOp: filteredData.preOp,
      pacu: filteredData.pacu,
      postOps: filteredData.postOps,
      hasPostop: derivedValues.hasPostop,
      hospitalId,
      preOpFocused: configuration?.preOpFocused,
      pacuFocused: configuration?.pacuFocused,
      hasNoteModule: derivedValues.hasNoteModule,
      hasPreOpPriorityModule: derivedValues.hasPreOpPriorityModule,
      isMini: false,
      isSuperAdmin,
      isLiaison,
      messagingView: configuration?.messagingScreen,
      scope,
      staffMode,
      showScheduledProcedureDuration: configuration?.showScheduledProcedureDuration,
      showLatePreOpIndicator: configuration?.showLatePreOpIndicator || !isKiosk,
      showStaffTracking: derivedValues.showStaffTracking,
      isKiosk,
    }),
    [
      orderedRooms,
      filteredData,
      derivedValues,
      hospitalId,
      configuration,
      isSuperAdmin,
      isLiaison,
      scope,
      staffMode,
      isKiosk,
      showProcedure,
    ]
  );

  return (
    <Fragment>
      <Helmet>
        <meta
          name="viewport"
          content={isKiosk ? 'width=1200, initial-scale=0' : 'width=device-width, initial-scale=1'}
        />
      </Helmet>

      <MonitorContent {...monitorContentProps} />

      {shouldShowClientUpdater && <ClientUpdater />}
    </Fragment>
  );
};

// Create the HOC-wrapped version to match legacy implementation
const MonitorModernWithHOCs = withRouter(withSession(unpackSessionObject)(withScope(MonitorModern)));

export default React.memo(
  MonitorModernWithHOCs,
  (prevProps: MonitorWithHOCsProps, nextProps: MonitorWithHOCsProps) =>
    prevProps.scope === nextProps.scope &&
    prevProps.session === nextProps.session &&
    prevProps.configuration === nextProps.configuration
);
